/**
 * Date constraints for HTML5 date inputs (<input type="date"> / MUI <TextField type="date">)
 * Enforces valid, sensible application date boundaries (FAIL-06) and prevents invalid/extreme dates (e.g. year 0001, year 9999).
 */

/**
 * Formats a Date object or timestamp into YYYY-MM-DD string.
 */
export const formatDateToISO = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Historical minimum date boundary for warehouse records (2020-01-01).
 */
export const DEFAULT_DATE_MIN = '2020-01-01';

/**
 * Dynamically returns today's current date in YYYY-MM-DD format.
 */
export const getTodayDateString = () => formatDateToISO(new Date());

/**
 * Dynamically returns a date N years in the future in YYYY-MM-DD format (for scheduled ship/order dates).
 */
export const getFutureMaxDateString = (yearsAhead = 1) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + yearsAhead);
  return formatDateToISO(d);
};

/**
 * Standard native HTML5 inputProps for historical/activity date inputs (min: 2020-01-01, max: today).
 */
export const getDateInputProps = (customMin = DEFAULT_DATE_MIN, customMax) => ({
  min: customMin || DEFAULT_DATE_MIN,
  max: customMax || getTodayDateString(),
});

/**
 * Standard native HTML5 inputProps for order/ship date inputs where future dates may be scheduled (min: 2020-01-01, max: today + 1 year).
 */
export const getFutureDateInputProps = (customMin = DEFAULT_DATE_MIN, customMax) => ({
  min: customMin || DEFAULT_DATE_MIN,
  max: customMax || getFutureMaxDateString(1),
});
